import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

/**
 * M3 (#573): recent enterprise-admin emulation activity for one partner — a
 * compensating-control / audit surface for the triage dashboard.
 *
 * Wraps the NEW partner-scoped, SECURITY-DEFINER read RPC
 * `partner_emulation_activity`. `enterprise_admin_emulation_log` is already
 * partner-scoped via `partner_id`, so the RPC filters on it directly (no
 * workspace join). Gated server-side on `analytics_read`. Rows arrive
 * newest-first; `forced_by_security` flags a security-forced closure.
 */

/** One emulation-log entry for a partner (partner_emulation_activity row). */
export interface PartnerEmulationActivityRow {
  id: string;
  admin_user_id: string;
  admin_email: string | null;
  target_user_id: string;
  target_email: string | null;
  started_at: string;
  ended_at: string | null;
  end_reason: string | null;
  reason: string;
  read_only: boolean;
  /** end_reason === 'forced_by_security' — a security-forced emulation exit. */
  forced_by_security: boolean;
}

/** Audit activity drifts slowly; align with the analytics rollup's staleness. */
const STALE_TIME_MS = 60_000;

export function useEnterpriseEmulationActivity(partnerId: string) {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.emulationActivity(partnerId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('partner_emulation_activity', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return (data ?? []) as PartnerEmulationActivityRow[];
    },
    enabled: !!partnerId,
    staleTime: STALE_TIME_MS,
  });
}
